"use client";

import { Typography } from "@mui/material";
import React, { forwardRef } from "react";
import type { ButtonHTMLAttributes, ReactNode } from "react";
import styled, { css, keyframes } from "styled-components";

// Types for button props
export type ButtonVariant = "cyan" | "purple" | "accent";
export type ButtonState =
  | "idle"
  | "hover"
  | "pressed"
  | "focus"
  | "loading"
  | "disabled";

// Props interface for the Button component
export interface ButtonProps
  extends Omit<ButtonHTMLAttributes<HTMLButtonElement>, "as"> {
  variant?: ButtonVariant;
  state?: ButtonState;
  isLoading?: boolean;
  isDisabled?: boolean;
  href?: string;
  as?: React.ElementType;
  children: ReactNode;
}

// Spinner animation for loading state
const rotate = keyframes`
  from {
    transform: rotate(0deg);
  }
  to {
    transform: rotate(360deg);
  }
`;

// Base button styles
const baseButtonStyles = css<{ $variant: ButtonVariant; $state: ButtonState }>`
  display: flex;
  justify-content: center;
  align-items: center;
  min-height: auto;
  padding: 20px 30px;
  border-radius: 14px;
  font-family:
    Manrope,
    -apple-system,
    Roboto,
    Helvetica,
    sans-serif;
  font-size: 16px;
  font-weight: 500;
  text-transform: uppercase;
  line-height: 1;
  border: none;
  cursor: pointer;
  transition: background-color 0.2s ease;
  white-space: nowrap;
  overflow: hidden;

  @media (max-width: 991px) {
    white-space: initial;
  }

  // Variant and state specific styles
  ${({ $variant, $state }) => {
    // Cyan variant styles
    if ($variant === "cyan") {
      switch ($state) {
        case "idle":
          return css`
            background-color: #141414;
            color: #FAFAFA;
          `;
        case "hover":
          return css`
            background-color: #FAFAFA;
            color: #141414;
          `;
        case "pressed":
          return css`
            background-color: #3D5AA0;
            color: #141414;
          `;
        case "focus":
          return css`
            background-color: #5378C3;
            color: #141414;
            border: 4px solid #6C63FF;
          `;
        case "loading":
          return css`
            background-color: #5378C3;
            color: #141414;
          `;
        case "disabled":
          return css`
            background-color: #F1F1F1;
            color: #9E9E9E;
            cursor: not-allowed;
          `;
        default:
          return css`
            background-color: #5378C3;
            color: #141414;
          `;
      }
    }
    // Purple variant styles
    else if ($variant === "purple") {
      switch ($state) {
        case "idle":
          return css`
            background-color: #9F96FF;
            color: #000000;
          `;
        case "hover":
          return css`
            background-color: #C7C0FF;
            color: #000000;
          `;
        case "pressed":
          return css`
            background-color: #6C63FF;
            color: #FFFFFF;
          `;
        case "focus":
          return css`
            background-color: #9F96FF;
            color: #000000;
            border: 4px solid #00D9FF;
          `;
        case "loading":
          return css`
            background-color: #9F96FF;
            color: #000000;
          `;
        case "disabled":
          return css`
            background-color: #F1F1F1;
            color: #9E9E9E;
            cursor: not-allowed;
          `;
        default:
          return css`
            background-color: #9F96FF;
            color: #000000;
          `;
      }
    }
    // Accent (green-cyan) variant styles
    else {
      switch ($state) {
        case "idle":
          return css`
            background-color: #00FF9D;
            color: #000000;
          `;
        case "hover":
          return css`
            background-color: #5CFFBD;
            color: #000000;
          `;
        case "pressed":
          return css`
            background-color: #00CC7D;
            color: #000000;
          `;
        case "focus":
          return css`
            background-color: #00FF9D;
            color: #000000;
            border: 4px solid #6C63FF;
          `;
        case "loading":
          return css`
            background-color: #00FF9D;
            color: #000000;
          `;
        case "disabled":
          return css`
            background-color: #F1F1F1;
            color: #9E9E9E;
            cursor: not-allowed;
          `;
        default:
          return css`
            background-color: #00FF9D;
            color: #000000;
          `;
      }
    }
  }}

  &:hover {
    ${({ $state, $variant }) => {
      if ($state === "disabled" || $state === "loading") return "";

      if ($variant === "cyan") {
        return css`
          background-color: #FAFAFA;
          color: #141414;
        `;
      } else if ($variant === "purple") {
        return css`
          background-color: #C7C0FF;
        `;
      } else {
        return css`
          background-color: #5CFFBD;
        `;
      }
    }}
  }

  &:active {
    ${({ $state, $variant }) => {
      if ($state === "disabled" || $state === "loading") return "";

      if ($variant === "cyan") {
        return css`
          background-color: #3D5AA0;
          color: #141414;
        `;
      } else if ($variant === "purple") {
        return css`
          background-color: #6C63FF;
          color: #FFFFFF;
        `;
      } else {
        return css`
          background-color: #00CC7D;
        `;
      }
    }}
  }

  &:focus-visible {
    ${({ $state, $variant }) => {
      if ($state === "disabled") return "";

      if ($variant === "cyan") {
        return css`
          background-color: #5378C3;
          color: #141414;
          border: 4px solid #6C63FF;
          outline: none;
        `;
      } else if ($variant === "purple") {
        return css`
          background-color: #9F96FF;
          border: 4px solid #00D9FF;
          outline: none;
        `;
      } else {
        return css`
          background-color: #00FF9D;
          border: 4px solid #6C63FF;
          outline: none;
        `;
      }
    }}
  }
`;

// Styled button component
const StyledButton = styled.button<{
  $variant: ButtonVariant;
  $state: ButtonState;
}>`
  ${baseButtonStyles}
`;

// Styled link component
const StyledLink = styled.a<{ $variant: ButtonVariant; $state: ButtonState }>`
  ${baseButtonStyles}
  text-decoration: none;
`;

// Spinner component for loading state
const SpinnerIcon = styled.div`
  width: 24px;
  height: 24px;
  animation: ${rotate} 1s linear infinite;

  svg {
    width: 100%;
    height: 100%;
  }
`;

// Spinner SVG
const Spinner = () => (
  <svg
    width="24"
    height="24"
    viewBox="0 0 24 24"
    fill="none"
    xmlns="http://www.w3.org/2000/svg"
  >
    <path
      d="M12 2C6.48 2 2 6.48 2 12C2 17.52 6.48 22 12 22C17.52 22 22 17.52 22 12C22 6.48 17.52 2 12 2ZM12 20C7.59 20 4 16.41 4 12C4 7.59 7.59 4 12 4C16.41 4 20 7.59 20 12C20 16.41 16.41 20 12 20Z"
      fill="currentColor"
      fillOpacity="0.3"
    />
    <path
      d="M12 2V4C16.41 4 20 7.59 20 12H22C22 6.48 17.52 2 12 2Z"
      fill="currentColor"
    />
  </svg>
);

// Button component
export const Button = forwardRef<
  HTMLButtonElement | HTMLAnchorElement,
  ButtonProps
>(
  (
    {
      variant = "cyan",
      state = "idle",
      isLoading = false,
      isDisabled = false,
      href,
      as,
      children,
      ...props
    },
    ref,
  ) => {
    // Determine the current state of the button
    const currentState: ButtonState = isDisabled
      ? "disabled"
      : isLoading
        ? "loading"
        : state;

    // If the button is a link (has href or as prop)
    if (href || as) {
      return (
        <StyledLink
          ref={ref as React.Ref<HTMLAnchorElement>}
          $variant={variant}
          $state={currentState}
          href={isDisabled ? undefined : href}
          as={as}
          tabIndex={isDisabled ? -1 : 0}
          aria-disabled={isDisabled}
          {...props}
        >
          {isLoading ? (
            <SpinnerIcon>
              <Spinner />
            </SpinnerIcon>
          ) : (
            <Typography variant="body7" sx={{
                color: "inherit",
            }}>
                {children}
            </Typography>
          )}
        </StyledLink>
      );
    }

    // Regular button
    return (
      <StyledButton
        ref={ref as React.Ref<HTMLButtonElement>}
        $variant={variant}
        $state={currentState}
        disabled={isDisabled}
        aria-busy={isLoading}
        {...props}
      >
        {isLoading ? (
          <SpinnerIcon>
            <Spinner />
          </SpinnerIcon>
        ) : (
          children
        )}
      </StyledButton>
    );
  },
);

Button.displayName = "Button";

export default Button;
